/**
 * CENTRALIZED DATA SYNC SYSTEM
 * 
 * This system manages all data relationships in the app.
 * When ANY data changes, all dependent areas are automatically notified.
 * 
 * DATA DEPENDENCY MAP:
 * ====================
 * 
 * JOBS changes affect:
 *   - Rounds (revenue, job counts, customer lists)
 *   - Customers (job lists, totals)
 *   - Schedule/WorkPlanner (daily job lists)
 *   - VisualPlanner (calendar view)
 *   - JobList (job table)
 *   - Overview/Dashboard (stats, charts)
 *   - Invoices (if job is invoiced)
 *   - Reporting (all reports)
 * 
 * CUSTOMERS changes affect:
 *   - Rounds (customer counts, if zone changes)
 *   - Jobs (customer name, address)
 *   - Invoices (customer details)
 *   - Overview/Dashboard (customer count)
 *   - Reporting
 * 
 * ROUNDS changes affect:
 *   - Jobs (if round/zone renamed)
 *   - Customers (if zone renamed)
 *   - Overview/Dashboard
 * 
 * TEAM/ASSIGNEES changes affect:
 *   - Jobs (assignee dropdown)
 *   - Schedule (worker filter)
 *   - WorkPlanner (assignments)
 * 
 * SERVICES changes affect:
 *   - Jobs (service dropdown)
 *   - Invoices (service names)
 *   - Reporting
 * 
 * INVOICES changes affect:
 *   - Customers (balance)
 *   - Overview/Dashboard (revenue)
 *   - Payments
 *   - Reporting
 * 
 * PAYMENTS changes affect:
 *   - Invoices (paid status)
 *   - Customers (balance)
 *   - Overview/Dashboard
 *   - Reporting
 * 
 * SETTINGS changes affect:
 *   - Everything (company logo, frequencies, etc.)
 */

// All data types in the system
export const DataTypes = {
  JOB: 'job',
  CUSTOMER: 'customer',
  ROUND: 'round',
  TEAM: 'team',
  SERVICE: 'service',
  INVOICE: 'invoice',
  PAYMENT: 'payment',
  QUOTE: 'quote',
  EXPENSE: 'expense',
  SETTINGS: 'settings',
};

// Define what each data type affects
const DEPENDENCY_MAP = {
  [DataTypes.JOB]: [
    'rounds',
    'customers', 
    'schedule',
    'workplanner',
    'visualplanner',
    'joblist',
    'overview',
    'dashboard',
    'invoices',
    'reporting',
    'owings',
  ],
  [DataTypes.CUSTOMER]: [
    'rounds',
    'jobs',
    'joblist',
    'invoices',
    'overview',
    'dashboard',
    'reporting',
    'customers',
    // Deleting/editing a customer cascades to their jobs server-side
    // (db.jobs.delete_many in routes/customers.py), so any view that
    // renders jobs must refetch — otherwise the stale customer/job tile
    // sits on screen until manual refresh.
    'workplanner',
    'schedule',
    'visualplanner',
    'owings',
  ],
  [DataTypes.ROUND]: [
    'rounds',
    'jobs',
    'joblist',
    'customers',
    'overview',
    'dashboard',
  ],
  [DataTypes.TEAM]: [
    'jobs',
    'joblist',
    'schedule',
    'workplanner',
    'team',
  ],
  [DataTypes.SERVICE]: [
    'jobs',
    'joblist',
    'invoices',
    'reporting',
    'services',
  ],
  [DataTypes.INVOICE]: [
    'customers',
    'invoices',
    'overview',
    'dashboard',
    'payments',
    'reporting',
    'owings',
  ],
  [DataTypes.PAYMENT]: [
    'invoices',
    'customers',
    'overview',
    'dashboard',
    'payments',
    'reporting',
    'owings',
  ],
  [DataTypes.QUOTE]: [
    'quotes',
    'customers',
    'overview',
  ],
  [DataTypes.EXPENSE]: [
    'expenses',
    'reporting',
    'overview',
    'dashboard',
  ],
  [DataTypes.SETTINGS]: [
    // Settings affect everything
    'rounds',
    'customers',
    'jobs',
    'joblist',
    'schedule',
    'workplanner',
    'visualplanner',
    'invoices',
    'overview',
    'dashboard',
    'reporting',
    'settings',
  ],
};

// Action types
export const Actions = {
  CREATE: 'create',
  UPDATE: 'update',
  DELETE: 'delete',
  BULK_CREATE: 'bulk_create',
  BULK_UPDATE: 'bulk_update',
  BULK_DELETE: 'bulk_delete',
};

/**
 * Emit a data change event that notifies all dependent components
 * @param {string} dataType - The type of data that changed (from DataTypes)
 * @param {string} action - The action performed (from Actions)
 * @param {any} data - The data that changed
 */
export const emitDataChange = (dataType, action, data = null) => {
  const affectedAreas = DEPENDENCY_MAP[dataType] || [];
  
  console.log(`[DataSync] ${dataType} ${action}:`, {
    affectedAreas,
    data: data?.id || data?.length || 'N/A'
  });
  
  // Store timestamp in localStorage for cross-page sync (when target page isn't mounted)
  localStorage.setItem(`last${dataType.charAt(0).toUpperCase() + dataType.slice(1)}Update`, Date.now().toString());
  
  // Emit specific event for this data type
  window.dispatchEvent(new CustomEvent(`${dataType}Changed`, {
    detail: { action, data, affectedAreas }
  }));
  
  // Emit generic refresh events for each affected area
  affectedAreas.forEach(area => {
    window.dispatchEvent(new CustomEvent(`refresh_${area}`, {
      detail: { source: dataType, action, data }
    }));
  });
  
  // Also emit a global data change event
  window.dispatchEvent(new CustomEvent('dataChanged', {
    detail: { dataType, action, data, affectedAreas }
  }));
};

/**
 * Subscribe to changes for a specific area
 * @param {string} area - The area name (e.g., 'rounds', 'customers')
 * @param {function} callback - Function to call when refresh is needed
 * @returns {function} Cleanup function to remove listener
 */
export const subscribeToRefresh = (area, callback) => {
  const handler = (event) => {
    console.log(`[DataSync] ${area} received refresh from ${event.detail.source}`);
    callback(event.detail);
  };
  
  window.addEventListener(`refresh_${area}`, handler);
  
  // Return cleanup function
  return () => {
    window.removeEventListener(`refresh_${area}`, handler);
  };
};

/**
 * Subscribe to changes for a specific data type
 * @param {string} dataType - The data type (from DataTypes)
 * @param {function} callback - Function to call when data changes
 * @returns {function} Cleanup function to remove listener
 */
export const subscribeToDataType = (dataType, callback) => {
  const handler = (event) => {
    callback(event.detail);
  };
  
  window.addEventListener(`${dataType}Changed`, handler);
  
  return () => {
    window.removeEventListener(`${dataType}Changed`, handler);
  };
};

/**
 * Subscribe to ALL data changes (useful for debugging or global state)
 * @param {function} callback - Function to call on any data change
 * @returns {function} Cleanup function
 */
export const subscribeToAllChanges = (callback) => {
  const handler = (event) => {
    callback(event.detail);
  };
  
  window.addEventListener('dataChanged', handler);
  
  return () => {
    window.removeEventListener('dataChanged', handler);
  };
};

/**
 * React hook for subscribing to data refreshes
 * Usage: useDataRefresh('rounds', () => fetchRounds());
 */
export const useDataRefresh = (area, callback) => {
  const { useEffect, useRef } = require('react');
  
  const callbackRef = useRef(callback);
  callbackRef.current = callback;
  
  useEffect(() => {
    const handler = (event) => {
      callbackRef.current(event.detail);
    };
    
    window.addEventListener(`refresh_${area}`, handler);
    
    return () => {
      window.removeEventListener(`refresh_${area}`, handler);
    };
  }, [area]);
};

// Convenience methods for common operations
export const jobChanged = (action, data) => emitDataChange(DataTypes.JOB, action, data);
export const customerChanged = (action, data) => emitDataChange(DataTypes.CUSTOMER, action, data);
export const roundChanged = (action, data) => emitDataChange(DataTypes.ROUND, action, data);
export const teamChanged = (action, data) => emitDataChange(DataTypes.TEAM, action, data);
export const serviceChanged = (action, data) => emitDataChange(DataTypes.SERVICE, action, data);
export const invoiceChanged = (action, data) => emitDataChange(DataTypes.INVOICE, action, data);
export const paymentChanged = (action, data) => emitDataChange(DataTypes.PAYMENT, action, data);
export const settingsChanged = (action, data) => emitDataChange(DataTypes.SETTINGS, action, data);

export default {
  DataTypes,
  Actions,
  emitDataChange,
  subscribeToRefresh,
  subscribeToDataType,
  subscribeToAllChanges,
  useDataRefresh,
  jobChanged,
  customerChanged,
  roundChanged,
  teamChanged,
  serviceChanged,
  invoiceChanged,
  paymentChanged,
  settingsChanged,
};
